import { getBackendUrl } from '@/lib/backendUrl';
import { getApiError } from '@/lib/utils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { PlugZap, Unplug, ShieldAlert, FlaskConical, Copy, MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import QboRegionVerificationPanel from './QboRegionVerificationPanel';

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Admin/Senior Scheduler only — connecting or disconnecting QBO here never
// creates, previews or sends an invoice. This is purely connection/config:
// OAuth with Intuit's Sandbox, then (once connected) AZ Region ↔ QBO
// Department verification. Invoice creation lives only in a job's own
// "Create in QBO (Sandbox)" dialog, and reads this connection's status —
// it never starts or changes the connection itself.
export default function QboSandboxSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showRegions, setShowRegions] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/qbo-live/status`, { headers: authHeaders() });
      setStatus(data);
    } catch (err) {
      toast.error(getApiError(err, 'Could not load the QBO Sandbox connection status.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API}/api/qbo-live/connect`, { headers: authHeaders() });
      window.open(data.authorization_url, '_blank', 'noopener,noreferrer');
      toast.message('Intuit Sandbox consent opened in a new tab — come back here and refresh once you approve it.');
    } catch (err) {
      toast.error(getApiError(err, 'QBO Sandbox is not configured yet.'));
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await axios.post(`${API}/api/qbo-live/disconnect`, {}, { headers: authHeaders() });
      toast.success('Disconnected from QBO Sandbox.');
      await load();
    } catch (err) {
      toast.error(getApiError(err, 'Could not disconnect.'));
    } finally {
      setDisconnecting(false);
    }
  };

  const copyCallback = () => {
    navigator.clipboard?.writeText(status?.callback_uri || '');
    toast.success('Callback URI copied.');
  };

  return (
    <div data-testid="qbo-sandbox-settings">
      <div className="flex items-center gap-2 mb-3">
        <FlaskConical className="h-5 w-5 text-rose-600" />
        <h2 className="text-lg font-semibold text-slate-900">QBO Sandbox connection</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Connects AbbeyZone directly to a QuickBooks Online <strong>Sandbox</strong> company only — this is config, not invoicing.
        It never creates, previews or sends an invoice; that happens separately from an individual job once this is connected.
        Production stays blocked on the server regardless of what this shows.
      </p>

      {loading && <div className="text-sm text-slate-500" data-testid="qbo-sandbox-settings-loading">Loading…</div>}

      {!loading && status && (
        <div className="space-y-4">
          {!status.configured && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900" data-testid="qbo-sandbox-settings-not-configured">
              <div className="font-semibold text-sm flex items-center gap-1.5 mb-2"><ShieldAlert className="h-4 w-4" />Not set up yet — Sandbox credentials are missing</div>
              <div className="text-xs mb-2">Missing: {status.missing_env_vars.join(', ')}</div>
              <div className="flex items-center gap-1.5 mb-2">
                <code className="text-xs bg-white border border-amber-200 rounded px-2 py-1 flex-1 break-all" data-testid="qbo-sandbox-settings-callback-uri">{status.callback_uri}</code>
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={copyCallback} data-testid="qbo-sandbox-settings-copy-callback-btn"><Copy className="h-3.5 w-3.5" /></Button>
              </div>
              <ol className="text-xs list-decimal list-inside space-y-0.5" data-testid="qbo-sandbox-settings-setup-steps">
                {status.setup_steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>
          )}

          {status.configured && !status.connected && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 space-y-3" data-testid="qbo-sandbox-settings-configured-not-connected">
              <div className="text-sm text-sky-900 flex items-center gap-1.5"><FlaskConical className="h-4 w-4" />Credentials staged — not yet connected to a Sandbox company.</div>
              <div className="flex items-center gap-1.5 mb-1">
                <code className="text-xs bg-white border border-sky-200 rounded px-2 py-1 flex-1 break-all" data-testid="qbo-sandbox-settings-callback-uri-connected">{status.callback_uri}</code>
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={copyCallback} data-testid="qbo-sandbox-settings-copy-callback-btn-2"><Copy className="h-3.5 w-3.5" /></Button>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" disabled={connecting} data-testid="qbo-sandbox-settings-connect-btn">
                    {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FlaskConical className="h-4 w-4 mr-1.5" />}Connect QBO Sandbox
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Connect to a QuickBooks SANDBOX company?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This opens Intuit's Sandbox consent screen in a new tab. It only ever authorises a test ("Sandbox") company —
                      Production QuickBooks cannot be selected and is blocked on the server regardless. No invoice is created by this step.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="qbo-sandbox-settings-connect-confirm-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={connect} data-testid="qbo-sandbox-settings-connect-confirm-btn">Continue to Intuit Sandbox</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button size="sm" variant="ghost" onClick={load} data-testid="qbo-sandbox-settings-refresh-btn">Refresh status</Button>
            </div>
          )}

          {status.connected && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-3" data-testid="qbo-sandbox-settings-connected">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="flex items-center gap-1.5 text-sm text-emerald-800" data-testid="qbo-sandbox-settings-connected-badge">
                  <PlugZap className="h-4 w-4" />Connected —
                  <Badge variant="outline" className="border-emerald-300 text-emerald-800">{status.environment}</Badge>
                  company {status.realm_id}
                </span>
                {status.environment !== 'sandbox' && (
                  <Badge variant="destructive" data-testid="qbo-sandbox-settings-unexpected-env-badge">Unexpected environment — report this</Badge>
                )}
              </div>
              <div className="text-xs text-emerald-700">
                Connected {status.connected_at ? new Date(status.connected_at).toLocaleString('en-GB') : '—'}.
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setShowRegions(true)} data-testid="qbo-sandbox-settings-verify-regions-btn">
                  <MapPin className="h-3.5 w-3.5 mr-1" />Verify regions
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" disabled={disconnecting} data-testid="qbo-sandbox-settings-disconnect-btn">
                      <Unplug className="h-3.5 w-3.5 mr-1" />Disconnect
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Disconnect this Sandbox company?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Jobs will no longer be able to create invoices directly in QBO until you reconnect. Nothing already created in QBO is affected.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="qbo-sandbox-settings-disconnect-confirm-cancel-btn">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={disconnect} data-testid="qbo-sandbox-settings-disconnect-confirm-btn">Disconnect</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}
        </div>
      )}

      {showRegions && <QboRegionVerificationPanel onClose={() => { setShowRegions(false); load(); }} />}
    </div>
  );
}
